import { useQuery } from '@apollo/client';
import i18n from '@risksmart-app/i18n/src/i18n';
import type { ControlRegisterResponse } from '@risksmart-app/trpc/src/types';
import type {
  Control_Bool_Exp,
  GetControlsQuery,
  Risk_Bool_Exp,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  GetControlsDocument,
  GetRisksFlatDocument,
  Parent_Type_Enum,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import type { UseQueryOptions } from '@tanstack/react-query';
import { useMemo } from 'react';
import { mapTrpcControlsToGraphQL } from 'src/hooks/queries/control/useGetControlsRegister';

import useEntityWhereFilter from '@/hooks/useEntityWhereFilter';
import type { StatefulTableOptions } from '@/utils/table/hooks/useGetStatelessTableProps';
import type { TableFields, TableRecord } from '@/utils/table/types';

import { UNRATED } from '../../gigawidget/types';
import { createDataSource } from '../createDataSource';
import { defaultClickthroughFilter } from '../dataSourceHelpers';
import { simpleField, useRelationshipTableProps } from './relationship-helpers';

export type RiskControlRow = TableRecord & {
  Id: string;
  RiskId: string;
  RiskTitle: string;
  RiskTier: string;
  ControlId: string;
  ControlTitle: string;
  ControlType: string;
};

export default createDataSource({
  group: 'relationship',
  hasAccess: () => true,
  parentTypes: [Parent_Type_Enum.Risk, Parent_Type_Enum.Control],
  documentNode: GetControlsDocument,
  trpcQuery: (trpc) => {
    const queryOptions = trpc.frontend.control.register.queryOptions({
      parentId: undefined,
    });
    const transformedQueryOptions = {
      ...queryOptions,
      select: (data: ControlRegisterResponse) => mapTrpcControlsToGraphQL(data),
    };

    return transformedQueryOptions as UseQueryOptions<unknown, Error>;
  },
  useDefaultVariables: () => ({
    where: useEntityWhereFilter<Control_Bool_Exp>(Parent_Type_Enum.Control),
  }),
  useTablePropsHook: (
    data: GetControlsQuery | undefined,
    options: StatefulTableOptions<RiskControlRow>
  ) => {
    const fields = useMemo<TableFields<RiskControlRow>>(
      () => ({
        RiskTitle: simpleField(i18n.t('risks.columns.risk_name')),
        RiskTier: simpleField(i18n.t('risks.columns.risk_tier')),
        ControlTitle: simpleField(i18n.t('controls.columns.title')),
        ControlType: simpleField(i18n.t('controls.columns.type')),
      }),
      []
    );

    const riskWhere = useEntityWhereFilter<Risk_Bool_Exp>(
      Parent_Type_Enum.Risk
    );
    const { data: riskData } = useQuery(GetRisksFlatDocument, {
      variables: { where: riskWhere, riskAssessmentResultsWhere: {} },
    });

    const joinedData = useMemo(() => {
      if (!data?.control || !riskData?.risk) {
        return undefined;
      }

      const riskMap = new Map(riskData.risk.map((r) => [r.Id, r]));

      return data.control.flatMap((control) =>
        (control.parents ?? [])
          .filter(
            (p) => p.parent?.ObjectType === Parent_Type_Enum.Risk && p.parent.Id
          )
          .map((parent) => {
            const risk = riskMap.get(parent.parent!.Id);

            return {
              Id: `${parent.parent!.Id}_${control.Id}`,
              RiskId: parent.parent!.Id,
              RiskTitle: risk?.Title ?? parent.risk?.Title ?? '',
              RiskTier: String(risk?.Tier ?? ''),
              ControlId: control.Id,
              ControlTitle: control.Title ?? '',
              ControlType: control.Type ?? '',
            };
          })
      );
    }, [data?.control, riskData?.risk]);

    return useRelationshipTableProps(
      joinedData,
      fields,
      i18n.t('risk_control_other'),
      options
    );
  },
  entityNamePlural: 'risk_control_other',
  entityNameSingular: 'risk_control_one',
  fields: 'riskControls.fields',
  dashboardFilterConfig: {},
  categoryGetters: [
    {
      id: 'riskTier',
      name: () => i18n.t('risks.columns.risk_tier'),
      categoryGetter: (item: RiskControlRow) => item.RiskTier || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('RiskTier'),
    },
    {
      id: 'controlType',
      name: () => i18n.t('controls.columns.type'),
      categoryGetter: (item: RiskControlRow) => item.ControlType || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('ControlType'),
    },
    {
      id: 'riskTitle',
      name: () => i18n.t('risks.columns.risk_name'),
      categoryGetter: (item: RiskControlRow) => item.RiskTitle || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('RiskTitle'),
    },
    {
      id: 'controlTitle',
      name: () => i18n.t('controls.columns.title'),
      categoryGetter: (item: RiskControlRow) => item.ControlTitle || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('ControlTitle'),
    },
  ],
});
